import { get, invoke, isEmpty } from 'lodash';
import {
  SURVEY_TYPE_SIMILAR_ON_EXPIRE,
  SURVEY_TYPE_ADDITIONAL_ON_EXPIRE,
  PARTICIPATION_STATUS,
} from '../../constants';
// eslint-disable-next-line import/no-cycle
import Survey from './index';
import request from '../../request';

const createNewSurvey = (survey) => {
  /* eslint-disable camelcase */
  const { lang, translations, s_id: surveyId } = survey;
  /* eslint-enable camelcase */
  if (!Pollfish.helper.json.translations[lang]) {
    Pollfish.helper.addTranslation({ lang, texts: translations });
  }
  Pollfish.helper.changeLanguage(lang);

  // Survey Viewed events are being handled by different integrations in different ways
  // We handle survey viewed here because surveys coming from the check status
  // will never be handled by each integration and we need to override them
  Pollfish.mobile.interface.sendToServer(
    '/v2/device/set/survey/viewed',
    `{"s_id": "${surveyId}"}`,
    false,
  );
  Pollfish.survey = new Survey(survey, { checkStatus: false });
  Pollfish.survey.start().catch(Pollfish.helper.handleError);
};

export const shouldCheckStatus = () =>
  Pollfish.helper.json.s_id &&
  !Pollfish.helper.isPreview &&
  !Pollfish.helper.isDebugMode();

const asyncCheckAndEnterSurvey = async ({
  isUserNotEligible,
  onSurveyContinue,
}) => {
  if (!shouldCheckStatus()) {
    onSurveyContinue();
    return;
  }
  const surveyId = Pollfish.helper.json.s_id;
  Pollfish.ui.showSurveyLoading();
  invoke(Pollfish, 'survey.setCheckAndEnterSurveyHasBeenCalledOnce');

  const {
    survey_response_type: surveyType,
    survey_session_id: surveySessionId,
    taxonomy,
    peeqDemographics,
    success,
    survey,
    quotas,
  } = await request({
    url: `/v2/surveys/${surveyId}/enter-survey`,
    method: 'POST',
    data: {
      isUserNotEligible,
    },
  });
  const userTaxonomy = isEmpty(peeqDemographics) ? taxonomy : peeqDemographics;

  // TODO: investigation only, remove after testing
  // all enter survey responses should contain survey_session_id
  if (!surveySessionId) {
    Raven.captureMessage('Missing survey session id', {
      extra: {
        ...Pollfish.helper.getRedirectData(),
      },
    });
  }
  // There is a case that while enter-survey has not finished
  // respondent is allowed to reject the survey which means that
  // the active survey will be a feedback survey, and should not
  // changed again
  if (Pollfish.survey && Pollfish.survey.isFeedbackSurvey()) {
    return;
  }

  // all enter survey responses should contain survey_session_id
  // even if respondent is not eligible to continue, this change
  // has been made for fraudo and straight-lining
  if (Pollfish.survey) {
    Pollfish.survey.setSurveySessionId(surveySessionId);
  } else {
    Raven.captureMessage('Unexpected flow: could not find existing survey', {
      extra: {
        ...Pollfish.helper.getRedirectData(),
      },
    });
  }

  if (success) {
    if (Pollfish.survey) {
      // many choice sets are called a block on the backend though.
      // Might need to change the naming here
      Pollfish.store.addTaxonomyData(userTaxonomy);
      Pollfish.store.addQuotasData(quotas);
    } else {
      Raven.captureMessage(
        'Unexpected flow: check or get similar has no survey',
      );
    }
    onSurveyContinue();
    return;
  }

  const containsSurvey = get(survey, 'containsSurvey', false);
  if (!containsSurvey) {
    Pollfish.helper.handleSurveyComplete({
      isUserNotEligible: true,
      disqualificationReason: PARTICIPATION_STATUS.SURVEY_EXPIRED,
      shouldGetInRouter: false,
      shouldCallSurveyComplete: false,
    });
    return;
  }

  if (surveyType === SURVEY_TYPE_SIMILAR_ON_EXPIRE) {
    createNewSurvey({
      ...survey,
      survey_session_id: surveySessionId,
      userTaxonomy,
      quotas,
    });
  } else if (surveyType === SURVEY_TYPE_ADDITIONAL_ON_EXPIRE) {
    Pollfish.ui.showSurveyExpiredScreen(() => {
      createNewSurvey({
        ...survey,
        survey_session_id: surveySessionId,
        userTaxonomy,
        quotas,
      });
    });
  }
};

const checkAndEnterSurvey = (props) => {
  asyncCheckAndEnterSurvey(props).catch(
    Pollfish.helper.handleGlobalPromiseError,
  );
};

export default checkAndEnterSurvey;
